import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  formatAuditDisplayValue,
  getAuditChangeFieldLabel,
  getAuditPayloadChanged,
  getAuditResultFlags,
  getPayloadMeta,
} from "@/lib/audit-log-display";

const PREVIEW_COUNT = 3;
const TRUNCATE_LEN = 120;

function truncateForUi(text: string, max: number): { display: string; full: string } {
  if (text.length <= max) return { display: text, full: text };
  return { display: `${text.slice(0, max)}…`, full: text };
}

function resolveFromToStrings(
  field: string,
  from: unknown,
  to: unknown,
  meta: Record<string, unknown>
): { fromRaw: string; toRaw: string } {
  let fromRaw = String(from ?? "");
  let toRaw = String(to ?? "");
  if (field === "measureTargetStageId" && meta.measureTargetStageLabels && typeof meta.measureTargetStageLabels === "object") {
    const m = meta.measureTargetStageLabels as { from?: unknown; to?: unknown };
    if (m.from != null) fromRaw = String(m.from);
    if (m.to != null) toRaw = String(m.to);
  }
  return { fromRaw, toRaw };
}

type Props = {
  t: TFunction;
  action: string;
  payload: unknown;
  /** List all change rows (e.g. detail sheet); hides inline expand/collapse. */
  expandAll?: boolean;
  /** Open a dialog when a truncated value is clicked. */
  valueDialog?: boolean;
};

export function AuditChangeSummary({ t, action, payload, expandAll = false, valueDialog = true }: Props) {
  const changed = getAuditPayloadChanged(payload);
  const meta = getPayloadMeta(payload);
  const [expanded, setExpanded] = useState(false);
  const [fullTextDialog, setFullTextDialog] = useState<{ title: string; body: string } | null>(null);
  const { failed, reason } = getAuditResultFlags(payload);

  const rows = useMemo(() => changed ?? [], [changed]);
  const displayRows = expandAll ? rows : expanded ? rows : rows.slice(0, PREVIEW_COUNT);

  const copyLines = () => {
    const text = rows
      .map((c) => {
        const lab = getAuditChangeFieldLabel(t, action, c.field);
        const { fromRaw, toRaw } = resolveFromToStrings(c.field, c.from, c.to, meta);
        return `${lab}: ${formatAuditDisplayValue(t, fromRaw)} → ${formatAuditDisplayValue(t, toRaw)}`;
      })
      .join("\n");
    void navigator.clipboard.writeText(text);
  };

  if (!rows.length) return null;

  return (
    <div className="space-y-2 text-sm">
      {failed ? (
        <div className="text-destructive text-xs font-medium">
          {reason
            ? t("settings.auditChangeFailedWithReason", { reason })
            : t("settings.auditChangeFailed")}
        </div>
      ) : null}

      {displayRows.map((c, i) => {
        const { fromRaw, toRaw } = resolveFromToStrings(c.field, c.from, c.to, meta);
        const fromDisp = formatAuditDisplayValue(t, fromRaw);
        const toDisp = formatAuditDisplayValue(t, toRaw);
        const fromT = truncateForUi(fromDisp, TRUNCATE_LEN);
        const toT = truncateForUi(toDisp, TRUNCATE_LEN);
        const fieldLabel = getAuditChangeFieldLabel(t, action, c.field);
        return (
          <div key={`${c.field}-${i}`} className="rounded-md border border-border/70 bg-muted/15 p-2.5">
            <div className="font-medium text-xs mb-2">{fieldLabel}</div>
            <div className="grid gap-2 text-xs">
              <div className="border-l-2 border-muted-foreground/30 pl-2">
                <span className="text-muted-foreground">{t("settings.auditBefore")}</span>
                <div className="mt-0.5 font-mono text-[11px] leading-snug">
                  {valueDialog && fromT.display !== fromT.full ? (
                    <button
                      type="button"
                      className="break-words text-left underline-offset-2 hover:underline w-full"
                      onClick={() =>
                        setFullTextDialog({
                          title: `${fieldLabel} · ${t("settings.auditBefore")}`,
                          body: fromT.full,
                        })
                      }
                    >
                      {fromT.display}
                    </button>
                  ) : (
                    <span className="break-words" title={fromT.display !== fromT.full ? fromT.full : undefined}>
                      {fromT.display}
                    </span>
                  )}
                </div>
              </div>
              <div className="border-l-2 border-primary/40 pl-2">
                <span className="text-muted-foreground">{t("settings.auditAfter")}</span>
                <div className="mt-0.5 font-mono text-[11px] leading-snug">
                  {valueDialog && toT.display !== toT.full ? (
                    <button
                      type="button"
                      className="break-words text-left underline-offset-2 hover:underline w-full"
                      onClick={() =>
                        setFullTextDialog({
                          title: `${fieldLabel} · ${t("settings.auditAfter")}`,
                          body: toT.full,
                        })
                      }
                    >
                      {toT.display}
                    </button>
                  ) : (
                    <span className="break-words" title={toT.display !== toT.full ? toT.full : undefined}>
                      {toT.display}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap gap-2 pt-0.5">
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={copyLines}>
          {t("settings.auditCopyChanges")}
        </Button>
        {!expandAll && rows.length > PREVIEW_COUNT ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded
              ? t("settings.auditShowLess")
              : t("settings.auditShowAllChanges", { count: rows.length })}
          </Button>
        ) : null}
      </div>
      <Dialog open={!!fullTextDialog} onOpenChange={(open) => !open && setFullTextDialog(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm font-normal leading-snug">{fullTextDialog?.title}</DialogTitle>
          </DialogHeader>
          <pre className="whitespace-pre-wrap break-words text-xs font-mono">{fullTextDialog?.body}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
